import { useLocale } from '@mirai/locale';
import { Notification, Progress, styles, Theme, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { APPROVED, SCRIPT_ID, SCRIPT_URL } from './FormNuvei.constants';
import { L10N } from './FormNuvei.l10n';
import * as style from './FormNuvei.module.css';
import { loadScript } from '../../../../../helpers/loadScript';

const CONTAINER_ID = 'nuvei-checkout';
const MODAL_LAYER = 'modal-layer';

export const FormNuvei = ({ response, ...others }) => {
  const { language, translate } = useLocale();

  const [error, setError] = useState();
  const [ready, setReady] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    let cleanup;

    (async () => {
      try {
        cleanup = await loadScript({
          domain: 'payments',
          id: SCRIPT_ID,
          src: SCRIPT_URL,
          onLoad: () => setReady(true),
        });
        if (mounted && window.checkout) setReady(true);
      } catch (nextError) {
        setError(nextError);
      }
    })();

    return () => {
      mounted = false;
      cleanup && cleanup();
    };
  }, []);

  useEffect(() => {
    const { modalLayer } = Theme.get();
    const layer = parseInt(modalLayer, 10);
    if (Number.isNaN(layer)) return;

    Theme.setVariable(MODAL_LAYER, `${layer - 1}`);

    return () => Theme.setVariable(MODAL_LAYER, modalLayer);
  }, []);

  useEffect(() => {
    if (!ready || !response) return;

    const { configuration: { cardHolderName, ...configuration } = {} } = response || {};

    try {
      setError();
      setPaymentReady(false);
      window.checkout?.({
        ...configuration,
        locale: language,
        renderTo: `#${CONTAINER_ID}`,
        theme: 'horizontal',
        ...(cardHolderName ? { fullName: cardHolderName } : undefined),
        onResult: handleResult,
      });
      setPaymentReady(true);
    } catch (nextError) {
      setError(nextError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  const handleError = () => {
    setPaymentReady(true);
  };

  const handleResult = ({ result, transactionId } = {}) => {
    if (result === APPROVED) {
      const { configuration: { returnUrl } = {} } = response || {};

      const url = new URL(returnUrl);
      transactionId && url.searchParams.append('transactionId', transactionId);

      window.location.href = url.toString();
    } else {
      handleError();
    }
  };

  return (
    <View {...others} className={styles(others.className, style.container)}>
      <Progress indeterminate visible={!paymentReady && !error} />
      <View id={CONTAINER_ID} className={style.checkout} />
      {error && (
        <Notification error small wide>
          {translate(L10N.NOTIFICATION_ERROR)}
        </Notification>
      )}
    </View>
  );
};

FormNuvei.displayName = 'Mirai:Payments:ButtonPayment:FormNuvei';

FormNuvei.propTypes = {
  response: PropTypes.shape({}),
};
